import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import {
    budgetTypeLabels,
    entityProfileTypeLabels,
    firmSizeLabels,
    industryLabels,
    meetingOptionLabels,
    projectTimelineLabels,
    revenueBracketLabels,
    servicesOfferedLabels,
    stageOfBusinessLabels,
} from "@components/entity/constants";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { Switch } from "@design-system/Switch/Switch";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useUpdateOrganizationProfileMutation } from "@api/organization.api";
import { pluralize } from "@common/helpers/string";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { FC, useCallback, useId, useState } from "react";
import { buildProfilePayload } from "./buildProfilePayload";
import { LogoUploadProvider } from "./LogoUploadProvider";
import { LogoSection } from "./LogoSection";
import { NameSection } from "./NameSection";
import { EditFirmSizeModal } from "./EditFirmSizeModal";
import { EditMatchingModal } from "./EditMatchingModal";
import { EditSellingModal } from "./EditSellingModal";
import styles from "./SettingsPage.module.scss";

function joinLabels(
    values: string[],
    labelMap: Record<string, string>,
): string | undefined {
    if (!values.length) {
        return undefined;
    }
    return values.map((v) => labelMap[v] ?? v).join(", ");
}

export const SettingsPage: FC = () => {
    const canManageSettings = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_SETTINGS,
    );
    const activeOrganization = useActiveOrganization();
    const { accountantMatchingEligibility } = useTypedFlags();
    const updateProfile = useUpdateOrganizationProfileMutation();
    const [showFirmBasics, setShowFirmBasics] = useState(false);
    const [showMatching, setShowMatching] = useState(false);
    const [showSelling, setShowSelling] = useState(false);
    const matchingEligibilityLabelId = useId();

    const handleEligibilityToggle = useCallback(() => {
        if (!activeOrganization) {
            return;
        }
        void updateProfile.mutateAsync({
            organizationId: activeOrganization.id,
            payload: buildProfilePayload(activeOrganization.profile, {
                accountantDirectoryEligible:
                    !activeOrganization.profile?.accountantDirectoryEligible,
            }),
        });
    }, [activeOrganization, updateProfile]);

    if (!canManageSettings || !activeOrganization) {
        return null;
    }

    const profile = activeOrganization.profile;

    const firmSizeLabel = profile?.firmSize
        ? firmSizeLabels[profile.firmSize]
        : undefined;
    const servicesLabel = joinLabels(
        profile?.servicesOffered ?? [],
        servicesOfferedLabels,
    );
    const firmBasicsSummary = [firmSizeLabel, servicesLabel]
        .filter(Boolean)
        .join(" · ");

    const matchingSummary = [
        joinLabels(
            profile?.supportedEntityTypes ?? [],
            entityProfileTypeLabels,
        ),
        joinLabels(profile?.stagesOfBusiness ?? [], stageOfBusinessLabels),
        joinLabels(profile?.revenueBrackets ?? [], revenueBracketLabels),
        joinLabels(profile?.industries ?? [], industryLabels),
        joinLabels(profile?.projectTimelines ?? [], projectTimelineLabels),
        joinLabels(profile?.budgets ?? [], budgetTypeLabels),
        joinLabels(profile?.meetingOptions ?? [], meetingOptionLabels),
    ]
        .filter(Boolean)
        .join(" · ");

    const sellingLinks = [
        profile?.websiteUrl,
        profile?.calendlyUrl,
        profile?.videoUrl,
        profile?.linkedinUrl,
        profile?.twitterUrl,
        profile?.youtubeUrl,
        profile?.googleReviewsUrl,
        profile?.samslistUrl,
        profile?.facebookUrl,
        profile?.yelpUrl,
    ].filter(Boolean);

    const sellingLinkCount = sellingLinks.length;
    const sellingSummary =
        sellingLinkCount > 0
            ? `${sellingLinkCount} ${pluralize("link", sellingLinkCount)} configured`
            : undefined;

    return (
        <>
            <article className={styles.settingsPageLayout}>
                <div className={styles.mainTitle}>
                    {activeOrganization.name}
                </div>
                <div className={styles.groupHeader}>Firm information</div>

                <Card stacked>
                    <NameSection organization={activeOrganization} />
                    <LogoUploadProvider>
                        <LogoSection />
                    </LogoUploadProvider>

                    <CardSection className={styles.section}>
                        <main>
                            <header className={styles.sectionHeader}>
                                Firm size &amp; services
                            </header>
                            {firmBasicsSummary && (
                                <span className={styles.sectionValue}>
                                    {firmBasicsSummary}
                                </span>
                            )}
                        </main>
                        <aside>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => setShowFirmBasics(true)}
                            >
                                Edit
                            </Button>
                        </aside>
                    </CardSection>
                </Card>

                <div className={styles.groupHeader}>
                    Who you want to work with
                </div>

                <Card stacked>
                    <CardSection className={styles.section}>
                        <main>
                            <header
                                id={matchingEligibilityLabelId}
                                className={styles.sectionHeader}
                            >
                                Matching eligibility
                            </header>
                            <span className={styles.sectionValue}>
                                Allow clients to discover your firm through the
                                accountant directory
                            </span>
                        </main>
                        <aside>
                            <Tooltip
                                content="Reach out to your account manager or support to learn how to receive client referrals"
                                disabled={accountantMatchingEligibility}
                            >
                                <span>
                                    <Switch
                                        checked={
                                            profile?.accountantDirectoryEligible ??
                                            false
                                        }
                                        onCheckedChange={
                                            handleEligibilityToggle
                                        }
                                        disabled={
                                            !accountantMatchingEligibility ||
                                            updateProfile.isPending
                                        }
                                        aria-labelledby={
                                            matchingEligibilityLabelId
                                        }
                                    />
                                </span>
                            </Tooltip>
                        </aside>
                    </CardSection>

                    <CardSection className={styles.section}>
                        <main>
                            <header className={styles.sectionHeader}>
                                Client matching preferences
                            </header>
                            {matchingSummary && (
                                <span className={styles.sectionValue}>
                                    {matchingSummary}
                                </span>
                            )}
                        </main>
                        <aside>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => setShowMatching(true)}
                            >
                                Edit
                            </Button>
                        </aside>
                    </CardSection>
                </Card>

                <div className={styles.groupHeader}>Public profile</div>

                <Card stacked>
                    <CardSection className={styles.section}>
                        <main>
                            <header className={styles.sectionHeader}>
                                Links &amp; description
                            </header>
                            {sellingSummary && (
                                <span className={styles.sectionValue}>
                                    {sellingSummary}
                                </span>
                            )}
                        </main>
                        <aside>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => setShowSelling(true)}
                            >
                                Edit
                            </Button>
                        </aside>
                    </CardSection>
                </Card>

                {showFirmBasics && (
                    <PlainModal
                        show={showFirmBasics}
                        onHide={() => setShowFirmBasics(false)}
                    >
                        <StandardModalHeader>
                            Edit firm size &amp; services
                        </StandardModalHeader>
                        <EditFirmSizeModal
                            organization={activeOrganization}
                            onClose={() => setShowFirmBasics(false)}
                        />
                    </PlainModal>
                )}

                {showMatching && (
                    <PlainModal
                        show={showMatching}
                        onHide={() => setShowMatching(false)}
                    >
                        <StandardModalHeader>
                            Edit matching preferences
                        </StandardModalHeader>
                        <EditMatchingModal
                            organization={activeOrganization}
                            onClose={() => setShowMatching(false)}
                        />
                    </PlainModal>
                )}

                {showSelling && (
                    <PlainModal
                        show={showSelling}
                        onHide={() => setShowSelling(false)}
                    >
                        <StandardModalHeader>
                            Edit links &amp; description
                        </StandardModalHeader>
                        <EditSellingModal
                            organization={activeOrganization}
                            onClose={() => setShowSelling(false)}
                        />
                    </PlainModal>
                )}
            </article>
        </>
    );
};
